import React from 'react';
import { motion } from 'framer-motion';
import ShareBlogButton from '../../../components/ShareBlogButton/ShareBlogButton';
import './employee-daily-task-allocation-management-system.css';
import employeeTaskImg from '../../../assets/images/employeedailytask .jpeg';

const EmployeeDailyTaskAllocationManagementSystem = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        Employee <span>Daily Task Allocation</span> Management System
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <br />
        Published On: 10th December 2024<br />
    🕒 Last Updated On: 10th December 2024
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={employeeTaskImg} alt="Employee Daily Task Allocation" className="project-image" />
          <h2 className="project-subtitle">Employee Daily Task Allocation Management System</h2>
          <span className="project-category">Software</span>
          <p className="project-author">Author: Praveen Kumar B</p>
          <p className="project-author">🕒3 Mins Read</p>
          <div className="blog-action-buttons">
            <a href="https://under-construction-work.netlify.app/" className="visit-project-button">→ Visit the Project</a>
            <ShareBlogButton
              blog={{
                id: "employee-daily-task-allocation-management-system",
                title: "Employee Daily Task Allocation Management System",
                path: "/blogs/blogdetail/employee-daily-task-allocation-management-system",
                category: "Software"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Introduction ✨</h3>
          <p className="overview-text">
            Every day, teams struggle with assigning, tracking, and managing tasks. As teams grow, spreadsheets and messages no longer cut it. That's when the idea of building a dedicated task allocation system came to life. In this blog, I’ll walk you through the journey of developing a system that helps companies bring clarity to daily operations.
          </p>

          <h3 className="section-title">The Problem We Wanted to Solve</h3>
          <p className="overview-description">
            In many organizations, employees often face unclear responsibilities, missed deadlines, and constant back-and-forth communication. Our goal was to fix this by creating a central hub where tasks could be assigned, updated, and tracked — all in real-time and without confusion.
          </p>

          <h3 className="section-title">Designing the Experience 🧠</h3>
          <p>
            We focused on creating an intuitive experience — clean dashboards, simple task creation, and automatic notifications. We made sure that whether you’re an employee tracking your day or a manager analyzing team performance, everything you need is just a click away.
          </p>

          <h3 className="section-title">Key Features That Made It Work</h3>
          <ul className="feature-list">
            <li><strong>Smart Task Assignment:</strong> Tasks can be categorized and prioritized with deadlines.</li>
            <li><strong>Personal Dashboards:</strong> Employees see only what’s relevant to them.</li>
            <li><strong>Instant Notifications:</strong> Keep everyone informed of updates.</li>
            <li><strong>Progress Reports:</strong> Visualize team performance with graphs and charts.</li>
            <li><strong>Built-in Collaboration:</strong> Share files and chat directly in the system.</li>
            <li><strong>Mobile Friendly:</strong> Access from any device, anywhere.</li>
            <li><strong>Tool Integration:</strong> Sync with Slack, Google Workspace, and more.</li>
          </ul>

          <h3 className="section-title">Why Teams Love It ❤️</h3>
          <ul className="benefits-list">
            <li>It helps boost productivity by cutting down on micromanagement.</li>
            <li>Time is managed more efficiently thanks to deadline reminders.</li>
            <li>Transparency builds trust across teams.</li>
            <li>Communication feels seamless and less chaotic.</li>
            <li>It’s scalable, whether you're a small startup or a large organization.</li>
          </ul>

          <h3 className="section-title">The Workflow in Action</h3>
          <ul className="how-it-works-list">
            <li>Admins set up roles and permissions.</li>
            <li>Managers allocate tasks using smart filters.</li>
            <li>Employees receive notifications and update progress.</li>
            <li>Analytics dashboards update automatically.</li>
            <li>Feedback loops allow for continuous improvement.</li>
          </ul>

          <h3 className="section-title">What Sets It Apart?</h3>
          <ul className="how-it-works-list">
            <li><strong>User-Centric Design:</strong> Everything is built around simplicity and speed.</li>
            <li><strong>Affordability:</strong> No hidden costs — it saves time and money.</li>
            <li><strong>Data Security:</strong> All information is encrypted and safe.</li>
            <li><strong>Fully Customizable:</strong> Adjust workflows as your team evolves.</li>
            <li><strong>Reliable Support:</strong> Get help any time, any day.</li>
          </ul>

          <h3 className="section-title">Final Thoughts 🎯</h3>
          <p>
            Building the <strong>Employee Daily Task Allocation Management System</strong> taught us a lot about the real challenges teams face every day. What started as a side idea turned into a tool that brings structure and visibility to workplaces. If you’re looking to simplify how your team handles daily work, give it a try. It just might be the productivity boost you’ve been waiting for.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default EmployeeDailyTaskAllocationManagementSystem;
